import React, { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, LayoutGrid } from 'lucide-react';
import { useSidebar } from '../../contexts/SidebarContext';
import MainLayout from '../layout/MainLayout';
import ContentArea from '../layout/ContentArea';
import Sidebar from '../layout/Sidebar';
import PillMenu from '../common/menu/PillMenu';

// Tabs
import ServicesSettings from './ServicesSettings';
import ProfileSettings from './ProfileSettings';

import styles from '../store/styles/StoreContainer.module.css';

const TABS = [
    { id: 'profile', label: 'Profile', icon: <User size={16} /> },
    { id: 'services', label: 'Services', icon: <LayoutGrid size={16} /> },
];

const TAB_PATHS = {
    profile: '/settings/profile',
    services: '/settings/services',
};

const DEFAULT_TAB = 'profile';

const SettingsContainer = () => {
    const [activeTab, setActiveTab] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { setPage: setSidebarPage } = useSidebar();

    const getInitialTab = useCallback(() => {
        const path = location.pathname;
        const tab = Object.entries(TAB_PATHS).find(([_, url]) => url === path);
        return tab ? tab[0] : DEFAULT_TAB;
    }, [location]);

    useEffect(() => {
        setActiveTab(getInitialTab());
    }, [getInitialTab]);

    useEffect(() => {
        document.title = 'JetChats - Settings';
        setSidebarPage('settings');
    }, [setSidebarPage]);

    const handleNewChat = useCallback(() => {
      navigate('/chats');
    }, [navigate]);

    const handleTabChange = useCallback(tabId => {
        setActiveTab(tabId);
        navigate(TAB_PATHS[tabId]);
    }, [navigate]);

    useEffect(() => {
        if (location.pathname === '/settings') {
            navigate(TAB_PATHS[DEFAULT_TAB]);
        }
    }, [location.pathname, navigate]);

    const renderTabContent = useCallback(() => {
        switch (activeTab) {
            case 'profile':
                return <ProfileSettings />;
            case 'services':
                return <ServicesSettings />;
            default:
                return null;
        }
    }, [activeTab]);

    return (
        <MainLayout>
            <Sidebar handleNewChat={handleNewChat} />
            <ContentArea>
                <div className={styles.container}>
                    <div className={styles.content}>
                        <div className={styles.header}>
                            <h1>Settings</h1>
                            <p>Configure your chat experience and preferences.</p>
                        </div>
                        <div className={styles.tabRow}>
                            <div className={styles.tabsWrapper}>
                                <PillMenu
                                    tabs={TABS}
                                    activeTab={activeTab}
                                    onTabChange={handleTabChange}
                                />
                            </div>
                        </div>
                        {renderTabContent()}
                    </div>
                </div>
            </ContentArea>
        </MainLayout>
    );
};

export default memo(SettingsContainer);
